import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ChatHeader from "../../components/chat-header";
import { useLayoutContext } from "../../layouts/general-layout";
import { RouterConstant } from "../../routers/index";
import Button from "../../components/button";
import { SearchExerciseService } from "../../services/search-exercise/search-exercise.service";
import { SelfTestService } from "../../services/self-test/self-test-service";
import TestExerciseCard from "../../components/test-exercise-card";
import SolutionStatusPopup from "../../components/solution-status-popup";
import { useSelfTestStore } from "../../utils/selfTestStore";
import { useExerciseStore } from "../../utils/exerciseStore";
import "./self-test-answer-preview.scss";

export default function Index() {
  const { t } = useLayoutContext();
  const navigate = useNavigate();
  const { ROUTERS_PATH } = RouterConstant;
  const { state } = useLocation();
  const { selectedExercise, testSummaryCount, idx } = state || {};

  const searchExerciseService = new SearchExerciseService();
  const selfTestService = new SelfTestService();

  const [isExerciseLoading, setIsExerciseLoading] = useState(false);
  const [isAnswersLoading, setIsAnswersLoading] = useState(false);
  const [showFullExercise, setShowFullExercise] = useState(false);
  const [showSolutionStatusPopup, setShowSolutionStatusPopup] = useState(false);

  const [exercise, setExercise] = useState<any>(null);
  const [answers, setAnswers] = useState<any[]>([]);
  const [correctAnswer, setCorrectAnswer] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);

  useEffect(() => {
    if (selectedExercise) {
      loadExercise(selectedExercise.exercise_id);
    }
  }, [selectedExercise]);

  const loadExercise = async (exercise_id: number) => {
    setIsExerciseLoading(true);
    setIsAnswersLoading(true);
    setExercise(null);
    setAnswers([]);
    try {
      const data = await searchExerciseService.getLiteExerciseContent(exercise_id);
      setExercise(data);
    } catch (error) {
      console.error("Failed to load exercise", error);
    } finally {
      setIsExerciseLoading(false);
    }

    try {
      const ans = await selfTestService.getExercise_test_answers(exercise_id);
      setAnswers(ans.exercise_answers);
      const correct = ans.exercise_answers.find((a: any) => a.is_correct);
      const correct_id = correct ? correct.answer_id : null;
      const stored = useSelfTestStore.getState().selected_answers;
      const userSelected = stored[exercise_id]?.selected_answer ?? null;

      setSelectedAnswer(userSelected);
      setCorrectAnswer(correct_id);
    } catch (error) {
      console.error("Failed to load answers", error);
    } finally {
      setIsAnswersLoading(false);
    }
  };

  const handleAnswerSelect = async (answer_id: number) => {
    setSelectedAnswer(answer_id);
    await searchExerciseService.update_user_test_exercise_answer({
      user_id: 1, // mock user
      exercise_id: exercise.exercise_id,
      selected_answer_id: answer_id,
    });
  };

  const handleBackClick = () => {
    navigate(ROUTERS_PATH.SELFTESTSUMMARY, { state: { testId: selectedExercise?.test_id } });
  };

  const handleCloseClick = () => {
    navigate(ROUTERS_PATH.SELFTESTSUMMARY, { state: { testId: selectedExercise?.test_id }, replace: true });
  }

  const handleWayToSolveClick = async () => {
    useExerciseStore.getState().setSelectedExerciseId(selectedExercise?.exercise_id || 0);
    useExerciseStore.getState().setSelectedExerciseName(selectedExercise?.exercise_file_name || "");
    navigate(ROUTERS_PATH.QUESTIONSTEPS, {
      state: {
        api: "solution_by_steps",
        payload: {
          exercise_id: selectedExercise?.exercise_id
        },
        from: ROUTERS_PATH.SELFTESTANSWERPREVIEW,
        previewPageState: {
          selectedExercise,
          idx,
          testSummaryCount,
          from: ROUTERS_PATH.SELFTESTANSWERPREVIEW
        }
      },
    });
  }

  return (
    <div className={`self-test-exercise-page ${showSolutionStatusPopup ? 'modal-overlay' : ''}`}>
      <ChatHeader
        chatHeading={t("testYourself")}
        onBackClick={() => handleBackClick()}
        currentIndex={idx + 1 || 0}
        totalExercises={testSummaryCount}
      />
      {selectedExercise && (
        <div
          key={selectedExercise.exercise_id}
          className={`exercise-item ${selectedExercise.answer_status === 1
            ? "correct"
            : selectedExercise.answer_status === 2 || selectedExercise.answer_status === 3
              ? "wrong"
              : ""
            }`}
        >
          <div className="exercise-item-header">
            <p>
              <span className="question-title">
                {t("question")} {idx + 1}
              </span>
              <span className="divider">•</span>
              <span className="answer-status">
                {selectedExercise.answer_status === 1
                  ? t("rightAnswer")
                  : selectedExercise.answer_status === 2 || selectedExercise.answer_status === 3
                    ? t("wrongAnswer")
                    : ""}
              </span>
            </p>
          </div>
          <p>{selectedExercise.exercise_display_title}</p>
        </div>
      )}

      <div className="self-test-exercise-content">
        <TestExerciseCard
          t={t}
          exercise={exercise}
          answers={answers}
          isExerciseLoading={isExerciseLoading}
          isAnswersLoading={isAnswersLoading}
          selectedAnswer={selectedAnswer}
          correctAnswer={correctAnswer}
          showFullExercise={showFullExercise}
          onAnswerSelect={handleAnswerSelect}
          onToggleContent={() => setShowFullExercise((prev) => !prev)}
          onShowStatusPopup={() => setShowSolutionStatusPopup(true)}
          isApprovalStatusVisible={true}
          isPreviewMode={true}
        />

        {!isExerciseLoading && (
          <div className="self-test-exercise-button">
            <Button type="button" text={t("closed")} outline={true} onClick={() => handleCloseClick()} />
            <Button type="button" text={t("wayToSolveExercise")} color="blue" onClick={() => handleWayToSolveClick()} />
          </div>
        )}
      </div>
      {showSolutionStatusPopup && (
        <SolutionStatusPopup onClose={() => setShowSolutionStatusPopup(false)} />
      )}
    </div>
  );
}
